import {ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {TenantOverview} from 'src/app/services/tenant.service';
import {delay, interval, mergeMap, Subscription} from 'rxjs';
import {TopbarService} from 'src/app/services/front/topbar.service';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {ChipsModule} from "../../global/chips/chips/chips.module";
import {NgIf, NgOptimizedImage} from "@angular/common";
import {AgentsDeploymentGraphComponent} from "./agents-deployed-graph/agents-deployment-graph.component";
import {AgentListComponent} from "./agent-list/agent-list.component";
import {TabsModule} from "../../global/tabs/tabs.module";
import {TabModule} from "../../global/tabs/tab/tab.module";
import {DateAgoPipe} from "../../../pipes/date/date-ago.pipe";
import {TabButtonComponent} from "../../global/tabs/tab-button/tab-button.component";
import {RightSliderService} from "../../../services/front/right-slider.service";
import {DesignSystemModule} from "../../design-system/design-system.module";
import {AgentSettingsWrapperComponent} from "./agents-settings/agent-settings-wrapper.component";
import {AgentsVersionsComponent} from "./agents-versions/agents-versions.component";
import {RefreshService} from "./refresh.service";
import {SnackbarModule} from "../../global/snackbar/snackbar.module";
import {MatDialog} from "@angular/material/dialog";
import {AgentDownload} from "./agent-download/agent-download.component";
import {AgentService, GetAgentStatisticsResponse} from "../../../services/back/agent.service";
import {CurrentTenantService} from "../../../services/front/current-tenant.service";
import {tap} from "rxjs/operators";
import {HOME_URL} from "../../../models/home/navigation.model";

@Component({
	selector: 'app-agents',
	templateUrl: './agents.component.html',
	imports: [
		RouterModule,
		ChipsModule,
		TranslateModule,
		AgentsDeploymentGraphComponent,
		AgentListComponent,
		TabsModule,
		TabModule,
		DateAgoPipe,
		TabButtonComponent,
		NgOptimizedImage,
		DesignSystemModule,
		AgentsVersionsComponent,
		SnackbarModule,
		NgIf
	],
	styleUrl: './agents.component.scss'
})
export class AgentsComponent implements OnInit, OnDestroy {

	@ViewChild('leftColumn', { static: false}) leftColumnRef: ElementRef;
	protected _initializing: boolean;
	protected _loading: boolean;
	protected agentStatistics: GetAgentStatisticsResponse;
	protected lastUpdate = new Date().toISOString();
	protected timeSinceLastUpdate = '';
	protected subscription: Subscription = new Subscription();
	protected tenant: TenantOverview;
	protected columnHeight: string = '500px';
	protected tableHeight: string = '450px';

	constructor(
		private agentService: AgentService,
		private cdr: ChangeDetectorRef,
		private currentTenantService: CurrentTenantService,
		private dialog: MatDialog,
		private refreshService: RefreshService,
		private rightSliderService: RightSliderService,
		private translateService: TranslateService,
		public activatedRoute: ActivatedRoute,
		private router: Router,
		private topBarService: TopbarService,
	) {}

	ngOnInit() {
		// Refresh view to update last data refresh
		this.subscription.add(interval(5000).subscribe(() => this.cdr.detectChanges()));

		setInterval(() => this.timeSinceLastUpdate = new DateAgoPipe().transform(this.lastUpdate), 1000);

		// Refresh every 10s
		this.subscription.add(interval(10000).subscribe(() => {
				this.refreshService.triggerRefresh();
				setTimeout(() => this.lastUpdate = new Date().toISOString());
		}));

		this.subscription.add(this.currentTenantService.getCurrentTenantChanges().pipe(
			tap(tenant => {
				if (tenant.configuration.role === 'read_only') {
					// Pas réussi a faire marcher le guard
					this.router.navigate([HOME_URL])
				}
			}),
			mergeMap(tenant => this.agentService.getAgentStatistics(tenant.configuration.id)),
			tap(agentStatistics => this.agentStatistics = agentStatistics),
			delay(100),
			tap(() => this.calculateLeftColumnHeight()),
		).subscribe());

		this.subscription.add(this.refreshService.onRefresh.pipe(
			mergeMap(tenant => this.currentTenantService.getCurrentTenantId()),
			mergeMap(tenant => this.agentService.getAgentStatistics(tenant)),
			tap(agentStatistics => this.agentStatistics = agentStatistics),
			tap(() => 	this.calculateLeftColumnHeight())
		).subscribe())

		this.topBarService.onTitleChange(this.translateService.instant('page.agent.title'), this.translateService.instant('page.agent.subtitle'));
	}


	private calculateLeftColumnHeight() {
		this.columnHeight = (this.leftColumnRef.nativeElement.offsetHeight - 1) + 'px';
		this.tableHeight = (this.leftColumnRef.nativeElement.offsetHeight - 100) + 'px';
	}

	onOpenWorkstationSettings() {
		this.rightSliderService.openComponent(AgentSettingsWrapperComponent)
	}

	onDisplayDocumentation() {
		const url = this.translateService.instant('page.agent.workstations.documentationUrl')
		window.open(url, '_blank');
	}

	onOpenDownloadPage() {
		this.dialog.open(AgentDownload, {data: {announcement: {}}, maxWidth: '1000px'});
	}

	get adviceMessage(): {message: string, version?: string} {
		if (this.agentStatistics?.workstationAgentCount === 0) {
			return { message: this.translateService.instant('page.agent.workstations.noAgentMessage')};
		} else if (!this.agentStatistics?.workstationHasAllAgentInLatestVersion) {
			return { message: this.translateService.instant('page.agent.workstations.notAllLastVersionMessage'), version: this.agentStatistics?.workstationAgentLatestVersion };
		} else {
			return { message: this.translateService.instant('page.agent.workstations.allVersionAreOkMessage') };
		}
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	protected readonly Date = Date;
}
